// =============================================================================
// /news (and /blog) - Newsroom index
// =============================================================================
// The newsroom holds two kinds of content:
//   1. Updates  - short announcements: product launches, partnerships, etc.
//                 Defined in the `updates` array below.
//   2. Articles - long-form posts from `blogPosts` (BlogPostPage.js), rendered
//                 as cards that link to /blog/:slug.
//
// Reuses the landing design system (SectionEyebrow / SectionTitle / BlogCard)
// so the page reads as part of the marketing site.
// =============================================================================

import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';

import SEO from '../../shared/components/SEO';
import { Footer } from '../../shared/components/Footer';
import { SiteHeader } from '../../shared/components/SiteHeader';
import { BRAND_SANS_FONT } from '../../shared/design/theme';

import { blogPosts, publishedDateISO } from './BlogPostPage';

import {
  LandingContainer,
  Section,
  Container,
  CenteredHeader,
  SectionEyebrow,
  SectionTitle,
  SectionSubtitle,
  BlogGrid,
  BlogCard,
  BlogCategory,
  BlogTitle,
  BlogMeta,
  BlogReadMore,
  BlogExcerpt,
} from '../home/HomePage.styles';

const ACCENT = '#B83B1D';

// Focus state for the linked cards. Both card types were reachable by keyboard
// and gave no sign of it.
const CARD_FOCUS = {
  '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: '3px' },
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
};

// Press + reduced-motion handling for the cards that lift on hover.
const CARD_LIFT_STATES = {
  ...CARD_FOCUS,
  '&:active': { transform: 'translateY(0)' },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
    '&:hover': { transform: 'none' },
  },
};

// Uppercase rail label shared by the two list sections.
const RAIL_LABEL = {
  fontFamily: BRAND_SANS_FONT,
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  mb: 2.5,
};

// Short-form announcements. Add new entries at the top (newest first).
// `to` is optional - when present the whole card links there.
const updates = [
  {
    type: 'Update',
    date: 'June 2026',
    title: 'New comparison guides: Restack vs Claude, Rogo, and Hebbia',
    description:
      'Side-by-side breakdowns of where Restack fits next to the tools deal teams already use.',
    to: '/vs/claude',
  },
  {
    type: 'Launch',
    date: 'March 2026',
    title: 'Restack is live on Microsoft AppSource',
    description:
      'Restack is certified and listed on Microsoft AppSource, so IT can deploy it across Microsoft 365 from the admin center.',
    to: '/security',
  },
];

// Derive a short excerpt from the first paragraph of a post.
function buildExcerpt(post) {
  const firstParagraph = post.content?.find((b) => b.type === 'paragraph');
  if (!firstParagraph?.text) return '';
  const text = firstParagraph.text.replace(/\s+/g, ' ').trim();
  if (text.length <= 180) return text;
  const sliced = text.slice(0, 180);
  const lastSpace = sliced.lastIndexOf(' ');
  return `${sliced.slice(0, lastSpace > 120 ? lastSpace : 180).trim()}…`;
}

export default function BlogIndexPage() {
  const theme = useTheme();
  const ink = theme.palette.text.primary;

  // Newest first. The object's declaration order put a March post above a May
  // one, so the newsroom opened on stale news.
  const posts = Object.entries(blogPosts)
    .map(([slug, post]) => ({ slug, ...post }))
    .sort((a, b) => publishedDateISO(b).localeCompare(publishedDateISO(a)));

  // Blog + BlogPosting list, matching the per-post BlogPosting on /blog/:slug.
  const blogSchema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Restack newsroom',
    url: 'https://re-stack.com/news',
    description:
      'Launches, partnerships, and writing from the Restack team on AI for private equity across PowerPoint, Excel, and Word.',
    publisher: {
      '@type': 'Organization',
      name: 'Restack',
      logo: { '@type': 'ImageObject', url: 'https://re-stack.com/logo.svg' },
    },
    blogPost: posts.map((post) => ({
      '@type': 'BlogPosting',
      headline: post.title,
      url: `https://re-stack.com/blog/${post.slug}`,
      datePublished: publishedDateISO(post),
      articleSection: post.category,
      author: { '@type': 'Organization', name: 'Restack', url: 'https://re-stack.com/' },
    })),
  };

  return (
    <LandingContainer data-theme="light" sx={{ position: 'relative' }}>
      <SEO
        title="News | Restack"
        description="Launches, partnerships, and writing from the Restack team on AI for private equity across PowerPoint, Excel, and Word."
        canonical="/news"
        schema={blogSchema}
      />

      {/* Shared marketing header - matches the landing page. */}
      <SiteHeader />

      <Section
        id="news-index"
        sx={{ pt: { xs: 'calc(56px + 1.5rem)', md: 'calc(64px + 3rem)' }, pb: { xs: 8, md: 12 } }}
      >
        <Container component="main" id="main-content" maxWidth="lg">
          <CenteredHeader>
            <SectionEyebrow>Newsroom</SectionEyebrow>
            <SectionTitle component="h1">What&rsquo;s <em>new</em></SectionTitle>
            <SectionSubtitle>
              Launches, partnerships, and writing from the Restack team on AI for private equity across PowerPoint, Excel, and Word.
            </SectionSubtitle>
          </CenteredHeader>

          {/* ── Updates ─────────────────────────────────────────────── */}
          {updates.length > 0 && (
            <Box sx={{ mt: { xs: 5, md: 7 } }}>
              <Typography component="h2" sx={{ ...RAIL_LABEL, color: alpha(ink, 0.5) }}>
                Latest updates
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {updates.map((u) => {
                  const linkProps = u.to
                    ? { component: RouterLink, to: u.to }
                    : {};
                  return (
                    <Box
                      key={u.title}
                      {...linkProps}
                      sx={{
                        display: 'block',
                        textDecoration: 'none',
                        p: { xs: 2.5, md: 3 },
                        borderRadius: '16px',
                        border: `1px solid ${alpha(ink, 0.1)}`,
                        backgroundColor: '#ffffff',
                        transition: 'border-color 0.2s ease, transform 0.2s ease',
                        ...(u.to && {
                          cursor: 'pointer',
                          '&:hover': { borderColor: alpha(ACCENT, 0.3), transform: 'translateY(-2px)' },
                          ...CARD_LIFT_STATES,
                        }),
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.25 }}>
                        <Box
                          component="span"
                          sx={{
                            fontFamily: BRAND_SANS_FONT,
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: ACCENT,
                            backgroundColor: alpha(ACCENT, 0.1),
                            borderRadius: '999px',
                            px: 1,
                            py: 0.4,
                            lineHeight: 1,
                          }}
                        >
                          {u.type}
                        </Box>
                        <Box
                          component="span"
                          sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.78rem', color: alpha(ink, 0.5) }}
                        >
                          {u.date}
                        </Box>
                      </Box>
                      <Typography
                        component="h3"
                        sx={{
                          fontFamily: BRAND_SANS_FONT,
                          fontSize: { xs: '1rem', md: '1.125rem' },
                          fontWeight: 600,
                          color: ink,
                          mb: 0.75,
                        }}
                      >
                        {u.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: BRAND_SANS_FONT,
                          fontSize: { xs: '0.8125rem', md: '0.875rem' },
                          lineHeight: 1.6,
                          color: theme.palette.text.secondary,
                        }}
                      >
                        {u.description}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* ── Articles ────────────────────────────────────────────── */}
          <Box sx={{ mt: { xs: 6, md: 9 } }}>
            <Typography component="h2" sx={{ ...RAIL_LABEL, color: alpha(ink, 0.5) }}>
              Articles
            </Typography>

            <BlogGrid>
              {posts.map((post) => (
                <BlogCard
                  key={post.slug}
                  as={RouterLink}
                  to={`/blog/${post.slug}`}
                  sx={{ textDecoration: 'none', ...CARD_FOCUS }}
                >
                  <BlogCategory>{post.category}</BlogCategory>
                  {/* h3 under the "Articles" h2: these were paragraphs, so the
                      list had no structure for a screen reader to walk. */}
                  <BlogTitle component="h3" className="blog-title">{post.title}</BlogTitle>
                  <BlogMeta>
                    {/* Date and read time share one flex child so the row's
                        gap does not strand the separator. The date was missing
                        entirely: nothing on the card said whether a post was
                        from this month or last year. */}
                    <Box component="span">
                      <Box component="time" dateTime={publishedDateISO(post)}>
                        {post.date}
                      </Box>
                      <Box component="span" aria-hidden="true" sx={{ opacity: 0.4, mx: 0.5 }}>
                        ·
                      </Box>
                      {post.readTime}
                    </Box>
                    <BlogReadMore className="blog-read-more">
                      Read &rarr;
                    </BlogReadMore>
                  </BlogMeta>
                  <BlogExcerpt>{buildExcerpt(post)}</BlogExcerpt>
                </BlogCard>
              ))}
            </BlogGrid>
          </Box>

          {/* Closing CTA. The newsroom previously ended on the last card, with
              no way back into the product. Same primary wording as the rest of
              the site. */}
          <Box
            sx={{
              mt: { xs: 6, md: 9 },
              mx: 'auto',
              maxWidth: 760,
              p: { xs: 3, md: 4 },
              borderRadius: '20px',
              textAlign: 'center',
              border: `1px solid ${alpha(ink, 0.09)}`,
              background: `linear-gradient(135deg, ${alpha(ACCENT, 0.05)} 0%, ${alpha('#FAF9F7', 0.6)} 100%)`,
            }}
          >
            <Typography
              component="h2"
              sx={{
                fontFamily: BRAND_SANS_FONT,
                fontSize: { xs: '1.05rem', md: '1.2rem' },
                fontWeight: 600,
                letterSpacing: '-0.015em',
                lineHeight: 1.35,
                color: ink,
                mb: 1,
              }}
            >
              Rather see it than read about it?
            </Typography>
            <Typography
              sx={{
                fontFamily: BRAND_SANS_FONT,
                fontSize: '0.9rem',
                lineHeight: 1.6,
                color: alpha(ink, 0.7),
                maxWidth: 460,
                mx: 'auto',
                mb: 2.5,
              }}
            >
              Fifteen minutes, your template, a real slide rebuilt live inside
              PowerPoint.
            </Typography>
            <Button
              component={RouterLink}
              to="/demo"
              endIcon={<ArrowForwardRounded sx={{ fontSize: '1rem' }} />}
              sx={{
                textTransform: 'none',
                fontFamily: BRAND_SANS_FONT,
                fontWeight: 600,
                fontSize: '0.88rem',
                borderRadius: '999px',
                px: 2.75,
                py: 1,
                color: '#FFFFFF',
                background: 'linear-gradient(135deg, #B83B1D 0%, #D4694E 100%)',
                boxShadow: '0 4px 14px rgba(184, 59, 29, 0.22)',
                transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.12s ease',
                '&:active': { transform: 'translateY(1px)' },
                '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: '3px' },
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                '&:hover': {
                  background: 'linear-gradient(135deg, #A63419 0%, #C25A42 100%)',
                  boxShadow: '0 6px 18px rgba(184, 59, 29, 0.3)',
                },
              }}
            >
              See it on your own deck
            </Button>
          </Box>
        </Container>
      </Section>

      <Footer />
    </LandingContainer>
  );
}
